import { LucideIcon, Inbox } from "lucide-react";
import { Button } from "./button";
import { cn } from "@/lib/utils";

/**
 * EmptyState — polish-queue task 3 of 4.
 *
 * The primitive existed but was using soft-rounded muted bubbles that
 * read like consumer SaaS. This version sits in the MOMENTUM vocabulary:
 *   - Icon in a square tile (rounded-xl), not a circle, matching the
 *     brand-mark / hero-numeral / KPI-card geometry.
 *   - Eyebrow utility for the optional pre-title label.
 *   - Title uses section-title (Clash Display, tight tracking).
 *   - Description in muted body, max-width capped at ~52ch for readability.
 *   - Optional secondary action (link-style) below the primary.
 *   - Compact variant for inline-row hints; default for page-level empties.
 *
 * The contract stays backwards-compatible: existing callers passing
 * `{ icon, title, description, action }` keep working.
 */

interface EmptyStateAction {
  label: string;
  onClick: () => void;
  /** Optional icon to render before the label. */
  icon?: LucideIcon;
}

interface EmptyStateProps {
  icon?: LucideIcon;
  /** Optional small label above the title (e.g. "No data"). */
  eyebrow?: string;
  title: string;
  description?: string;
  /** Primary CTA. Mutually compatible with `actions` — both render. */
  action?: EmptyStateAction;
  /** Optional second equal-weight CTA, rendered next to `action`.
      Use when there are two genuinely equal next-steps (e.g. Earnings:
      "Log a session" + "Add earning"). Renders as outline button. */
  secondaryCta?: EmptyStateAction;
  /** Optional tertiary link-style action below the buttons. Use for
      "Learn more" / "Why am I seeing this?" — NOT for real CTAs. */
  link?: EmptyStateAction;
  className?: string;
  /** Compact layout — horizontal row with small icon, for inline use. */
  compact?: boolean;
}

export function EmptyState({
  icon: Icon = Inbox,
  eyebrow,
  title,
  description,
  action,
  secondaryCta,
  link,
  className,
  compact = false,
}: EmptyStateProps) {
  if (compact) {
    return (
      <div
        className={cn(
          "flex items-center gap-3 p-4 rounded-lg bg-muted/40 border border-dashed border-border/60",
          className,
        )}
      >
        <div className="h-10 w-10 rounded-lg bg-background border border-border/60 flex items-center justify-center flex-shrink-0">
          <Icon className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground tracking-tight">
            {title}
          </p>
          {description && (
            <p className="text-xs text-muted-foreground leading-snug">
              {description}
            </p>
          )}
        </div>
        {action && (
          <Button variant="outline" size="sm" onClick={action.onClick}>
            {action.label}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center px-6 py-12 text-center",
        className,
      )}
    >
      <div className="h-14 w-14 rounded-xl bg-muted/50 border border-border/60 flex items-center justify-center mb-5">
        <Icon className="h-6 w-6 text-muted-foreground" />
      </div>
      {eyebrow && (
        <span className="eyebrow text-muted-foreground/80 mb-2">{eyebrow}</span>
      )}
      <h3 className="section-title text-lg mb-2">{title}</h3>
      {description && (
        <p className="text-sm text-muted-foreground leading-relaxed max-w-[52ch] mb-5">
          {description}
        </p>
      )}
      {(action || secondaryCta) && (
        <div className="flex flex-col sm:flex-row gap-3 mt-1">
          {action && (
            <Button onClick={action.onClick}>
              {action.icon && <action.icon className="h-4 w-4" />}
              {action.label}
            </Button>
          )}
          {secondaryCta && (
            <Button variant="outline" onClick={secondaryCta.onClick}>
              {secondaryCta.icon && <secondaryCta.icon className="h-4 w-4" />}
              {secondaryCta.label}
            </Button>
          )}
        </div>
      )}
      {link && (
        <button
          onClick={link.onClick}
          className="mt-3 text-xs text-muted-foreground hover:text-foreground underline underline-offset-4 transition-colors"
        >
          {link.label}
        </button>
      )}
    </div>
  );
}
